import type { ApiResource } from "../../shared/api/resource.js";

export const activityTypes = [
  "decision",
  "order-submitted",
  "order-acknowledged",
  "order-filled",
  "order-cancelled",
  "order-rejected",
  "reconciliation",
  "lifecycle",
  "error",
] as const;

export type ActivityType = (typeof activityTypes)[number];

export interface ActivityView {
  readonly botId?: string;
  readonly botRunId?: string;
  readonly brokerAccountId?: string;
  readonly correlationId: string;
  readonly details: Readonly<Record<string, string>>;
  readonly fillId?: string;
  readonly id: string;
  readonly occurredAt: string;
  readonly orderId?: string;
  readonly status?: string;
  readonly summary: string;
  readonly symbol?: string;
  readonly type: ActivityType;
}

const sensitive = /authorization|client.?secret|credential|password|refresh.?token|access.?token/i;

function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : "";
}

function identifier(resource: ApiResource, field: string): string | undefined {
  const value = text(resource[field]);
  return value === "" ? undefined : value;
}

function safeDetails(value: unknown): Readonly<Record<string, string>> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return {};
  const result: Record<string, string> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (sensitive.test(key)) continue;
    const display = text(item);
    if (display !== "") result[key] = display;
  }
  return result;
}

function activityType(value: unknown): ActivityType {
  return activityTypes.includes(value as ActivityType) ? (value as ActivityType) : "error";
}

export function activityView(resource: ApiResource): ActivityView {
  const details = safeDetails(resource.details);
  const status = details.orderStatus ?? details.status;
  const symbol = text(resource.symbol) || details.symbol;
  const botId = identifier(resource, "botId");
  const botRunId = identifier(resource, "botRunId");
  const brokerAccountId = identifier(resource, "brokerAccountId");
  const fillId = identifier(resource, "fillId");
  const orderId = identifier(resource, "orderId");
  return {
    ...(botId === undefined ? {} : { botId }),
    ...(botRunId === undefined ? {} : { botRunId }),
    ...(brokerAccountId === undefined ? {} : { brokerAccountId }),
    correlationId: text(resource.correlationId),
    details,
    ...(fillId === undefined ? {} : { fillId }),
    id: resource.id,
    occurredAt: text(resource.occurredAt),
    ...(orderId === undefined ? {} : { orderId }),
    ...(status === undefined ? {} : { status }),
    summary: text(resource.summary) || "Activity recorded",
    ...(symbol === "" ? {} : { symbol }),
    type: activityType(resource.type),
  };
}
